import {
  httpsCallable,
  getFunctions,
  connectFunctionsEmulator,
} from "firebase/functions";
import type {
  AIMessage,
  ParsedAIResponse,
  User,
  ActivityRequest,
  SearchAIResponse,
} from "../types";
import { app, analytics, crashlytics } from "./firebase";
import {
  USE_EMULATOR,
  EMULATOR_HOST,
  EMULATOR_PORTS,
} from "../config/emulator";

const functions = app ? getFunctions(app, "asia-southeast1") : null;

// ============================================================================
// EMULATOR CONFIGURATION FOR FUNCTIONS
// ============================================================================
if (USE_EMULATOR && functions) {
  try {
    connectFunctionsEmulator(
      functions,
      EMULATOR_HOST,
      EMULATOR_PORTS.functions,
    );
    console.log(
      `✅ Functions Emulator: http://${EMULATOR_HOST}:${EMULATOR_PORTS.functions}`,
    );
  } catch (error) {
    console.warn("Functions emulator already connected or failed:", error);
  }
}
// ============================================================================

export type ActivityPayload = {
  id: string;
  title: string;
  description?: string;
  category: string;
  creatorId: string;
  participantIds: string[];
  groupSize: number;
  duration: number;
  dateTime: string;
  createdAt: string;
  location?: string;
  locationAddress?: string;
  coordinates?: { lat: number; lng: number } | null;
  imageUrl?: string;
  isPrivate?: boolean;
  isDeleted?: boolean;
  isActive?: boolean;
  isReported?: boolean;
  chatId?: string;
  reason?: string;
};

type HomeFeedRequest = {
  cursor?: string | null;
  pageSize?: number;
  category?: string;
};

export type HomeFeedResponse = {
  activities: ActivityPayload[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type ForYouResponse = {
  recommendations: Array<ActivityPayload & { reason?: string }>;
  vibeCheckUsers: Array<{
    user: {
      id: string;
      username: string;
      email: string;
      profileImageUrl: string;
      location: string;
      isAccountVerifiedForBadge: boolean;
      passions?: string[];
      interests?: string[];
      following?: string[];
      lastActiveAt?: string;
      userProfile?: unknown;
    };
    vibe: {
      score: number;
      summary: string;
      one_liner: string;
    };
  }>;
};

export async function getHomeFeedPage(
  payload: HomeFeedRequest = {},
): Promise<HomeFeedResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<HomeFeedRequest, HomeFeedResponse>(
    functions,
    "getHomeFeedPage",
  );
  try {
    await analytics.logEvent("call_function", { function: "getHomeFeedPage", payload });
    await crashlytics.log(`call_function:getHomeFeedPage:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getHomeFeedPage", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getHomeFeedPage", e);
    throw e;
  }
}

// Fetch single activity by ID
type GetActivityByIdRequest = {
  activityId: string;
};

export type GetActivityByIdResponse = {
  success: boolean;
  activity: ActivityPayload | null;
};

export async function getActivityById(
  payload: GetActivityByIdRequest,
): Promise<GetActivityByIdResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    GetActivityByIdRequest,
    GetActivityByIdResponse
  >(functions, "getActivityById");
  try {
    await analytics.logEvent("call_function", { function: "getActivityById", payload });
    await crashlytics.log(`call_function:getActivityById:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getActivityById", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getActivityById", e);
    throw e;
  }
}

// Per-tab activity endpoints
type UserCreatedActivitiesRequest = {
  userId: string;
};

type UserJoinedActivitiesRequest = {
  userId: string;
};

type UserExpiredActivitiesRequest = {
  userId: string;
};

type UserDeletedActivitiesRequest = {
  userId: string;
};

type UserSingleListResponse = {
  success: boolean;
  activities: ActivityPayload[];
};

export async function getUserCreatedActivities(
  payload: UserCreatedActivitiesRequest,
): Promise<UserSingleListResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UserCreatedActivitiesRequest,
    UserSingleListResponse
  >(functions, "getUserCreatedActivities");
  try {
    await analytics.logEvent("call_function", { function: "getUserCreatedActivities", payload });
    await crashlytics.log(`call_function:getUserCreatedActivities:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getUserCreatedActivities", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getUserCreatedActivities", e);
    throw e;
  }
}

export async function getUserJoinedActivities(
  payload: UserJoinedActivitiesRequest,
): Promise<UserSingleListResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UserJoinedActivitiesRequest,
    UserSingleListResponse
  >(functions, "getUserJoinedActivities");
  try {
    await analytics.logEvent("call_function", { function: "getUserJoinedActivities", payload });
    await crashlytics.log(`call_function:getUserJoinedActivities:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getUserJoinedActivities", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getUserJoinedActivities", e);
    throw e;
  }
}

export async function getUserExpiredActivities(
  payload: UserExpiredActivitiesRequest,
): Promise<UserSingleListResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UserExpiredActivitiesRequest,
    UserSingleListResponse
  >(functions, "getUserExpiredActivities");
  try {
    await analytics.logEvent("call_function", { function: "getUserExpiredActivities", payload });
    await crashlytics.log(`call_function:getUserExpiredActivities:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getUserExpiredActivities", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getUserExpiredActivities", e);
    throw e;
  }
}

export async function getUserDeletedActivities(
  payload: UserDeletedActivitiesRequest,
): Promise<UserSingleListResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UserDeletedActivitiesRequest,
    UserSingleListResponse
  >(functions, "getUserDeletedActivities");
  try {
    await analytics.logEvent("call_function", { function: "getUserDeletedActivities", payload });
    await crashlytics.log(`call_function:getUserDeletedActivities:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getUserDeletedActivities", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getUserDeletedActivities", e);
    throw e;
  }
}

type UserDeclinedActivitiesRequest = {
  userId: string;
};

export type UserDeclinedActivitiesResponse = {
  success: boolean;
  declined: ActivityPayload[];
};

export async function getUserDeclinedActivities(
  payload: UserDeclinedActivitiesRequest,
): Promise<UserDeclinedActivitiesResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UserDeclinedActivitiesRequest,
    UserDeclinedActivitiesResponse
  >(functions, "getUserDeclinedActivities");
  try {
    await analytics.logEvent("call_function", { function: "getUserDeclinedActivities", payload });
    await crashlytics.log(`call_function:getUserDeclinedActivities:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getUserDeclinedActivities", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getUserDeclinedActivities", e);
    throw e;
  }
}

type UserActivityStatsRequest = {
  userId: string;
};

export type UserActivityStatsResponse = {
  success: boolean;
  stats: {
    activitiesCreated: number;
    activitiesJoined: number;
    activitiesExpired: number;
    activitiesDeclined: number;
    activitiesDeleted: number;
    allActivitiesCount: number;
  };
};

export async function getUserActivityStats(
  payload: UserActivityStatsRequest,
): Promise<UserActivityStatsResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UserActivityStatsRequest,
    UserActivityStatsResponse
  >(functions, "getUserActivityStats");
  try {
    await analytics.logEvent("call_function", { function: "getUserActivityStats", payload });
    await crashlytics.log(`call_function:getUserActivityStats:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getUserActivityStats", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getUserActivityStats", e);
    throw e;
  }
}

// Fetch minimal activity metadata for smart caching
type ActivityMetadata = {
  id: string;
  updatedAt: string;
  participantIds: string[];
  isActive: boolean;
  isDeleted: boolean;
};

type GetActivitiesMetadataRequest = {
  activityIds: string[];
};

export type GetActivitiesMetadataResponse = {
  success: boolean;
  metadata: ActivityMetadata[];
};

export async function getActivitiesMetadata(
  payload: GetActivitiesMetadataRequest,
): Promise<GetActivitiesMetadataResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    GetActivitiesMetadataRequest,
    GetActivitiesMetadataResponse
  >(functions, "getActivitiesMetadata");
  try {
    await analytics.logEvent("call_function", { function: "getActivitiesMetadata", payload });
    await crashlytics.log(`call_function:getActivitiesMetadata:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getActivitiesMetadata", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getActivitiesMetadata", e);
    throw e;
  }
}

export async function getForYouRecommendations(): Promise<ForYouResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<Record<string, never>, ForYouResponse>(
    functions,
    "getForYouRecommendations",
  );
  try {
    await analytics.logEvent("call_function", { function: "getForYouRecommendations" });
    await crashlytics.log(`call_function:getForYouRecommendations`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getForYouRecommendations", e);
  }
  try {
    const result = await callable({});
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getForYouRecommendations", e);
    throw e;
  }
}

type GenerateActivitySuggestionRequest = {
  conversationHistory: AIMessage[];
};

export type GenerateActivitySuggestionResponse = {
  response: ParsedAIResponse;
};

export async function generateActivitySuggestion(
  payload: GenerateActivitySuggestionRequest,
): Promise<GenerateActivitySuggestionResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    GenerateActivitySuggestionRequest,
    GenerateActivitySuggestionResponse
  >(functions, "generateActivitySuggestion");
  try {
    await analytics.logEvent("call_function", { function: "generateActivitySuggestion", payload });
    await crashlytics.log(`call_function:generateActivitySuggestion:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "generateActivitySuggestion", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "generateActivitySuggestion", e);
    throw e;
  }
}

type GenerateActivitySearchIdeasRequest = {
  query: string;
  user?: User;
  userActivities?: ActivityRequest[];
  searchHistory?: string[];
};

export async function generateActivitySearchIdeas(
  payload: GenerateActivitySearchIdeasRequest,
): Promise<SearchAIResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    GenerateActivitySearchIdeasRequest,
    SearchAIResponse
  >(functions, "generateActivitySearchIdeas");
  try {
    await analytics.logEvent("call_function", { function: "generateActivitySearchIdeas", payload });
    await crashlytics.log(`call_function:generateActivitySearchIdeas:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "generateActivitySearchIdeas", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "generateActivitySearchIdeas", e);
    throw e;
  }
}

type CreateActivityRequest = {
  title: string;
  description?: string;
  category: string;
  duration: number;
  groupSize: number;
  dateTime: string;
  location?: string;
  locationAddress?: string;
  coordinates?: { lat: number; lng: number } | null;
  imageUrl?: string;
  isPrivate?: boolean;
};

export type CreateActivityResponse = {
  success: boolean;
  activityId: string;
  chatId: string;
  activity: ActivityPayload;
};

export async function createActivity(
  payload: CreateActivityRequest,
): Promise<CreateActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<CreateActivityRequest, CreateActivityResponse>(
    functions,
    "createActivity",
  );
  try {
    await analytics.logEvent("call_function", { function: "createActivity", payload });
    await crashlytics.log(`call_function:createActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "createActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "createActivity", e);
    throw e;
  }
}

type JoinActivityRequest = {
  activityId: string;
};

export type JoinActivityResponse = {
  success: boolean;
  isFull: boolean;
  alreadyJoined?: boolean;
};

export async function joinActivity(
  payload: JoinActivityRequest,
): Promise<JoinActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<JoinActivityRequest, JoinActivityResponse>(
    functions,
    "joinActivity",
  );
  try {
    await analytics.logEvent("call_function", { function: "joinActivity", payload });
    await crashlytics.log(`call_function:joinActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "joinActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "joinActivity", e);
    throw e;
  }
}

type UpdateActivityRequest = {
  id: string;
  title?: string;
  description?: string;
  category?: string;
  duration?: number;
  groupSize?: number;
  dateTime?: string;
  location?: string;
  locationAddress?: string;
  coordinates?: { lat: number; lng: number } | null;
  imageUrl?: string;
  isPrivate?: boolean;
};

export type UpdateActivityResponse = {
  success: boolean;
};

export async function updateActivity(
  payload: UpdateActivityRequest,
): Promise<UpdateActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<UpdateActivityRequest, UpdateActivityResponse>(
    functions,
    "updateActivity",
  );
  try {
    await analytics.logEvent("call_function", { function: "updateActivity", payload });
    await crashlytics.log(`call_function:updateActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "updateActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "updateActivity", e);
    throw e;
  }
}

type LeaveActivityRequest = {
  activityId: string;
};

export type LeaveActivityResponse = {
  success: boolean;
  newParticipantCount: number;
};

export async function leaveActivity(
  payload: LeaveActivityRequest,
): Promise<LeaveActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<LeaveActivityRequest, LeaveActivityResponse>(
    functions,
    "leaveActivity",
  );
  try {
    await analytics.logEvent("call_function", { function: "leaveActivity", payload });
    await crashlytics.log(`call_function:leaveActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "leaveActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "leaveActivity", e);
    throw e;
  }
}

type KickParticipantRequest = {
  activityId: string;
  participantId: string;
};

export type KickParticipantResponse = {
  success: boolean;
  newParticipantCount: number;
};

export async function kickParticipant(
  payload: KickParticipantRequest,
): Promise<KickParticipantResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    KickParticipantRequest,
    KickParticipantResponse
  >(functions, "kickParticipant");
  try {
    await analytics.logEvent("call_function", { function: "kickParticipant", payload });
    await crashlytics.log(`call_function:kickParticipant:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "kickParticipant", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "kickParticipant", e);
    throw e;
  }
}

type PassActivityRequest = {
  activityId: string;
};

export type PassActivityResponse = {
  success: boolean;
};

export async function passActivity(
  payload: PassActivityRequest,
): Promise<PassActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<PassActivityRequest, PassActivityResponse>(
    functions,
    "passActivity",
  );
  try {
    await analytics.logEvent("call_function", { function: "passActivity", payload });
    await crashlytics.log(`call_function:passActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "passActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "passActivity", e);
    throw e;
  }
}

type ApproveRejoinRequest = {
  activityId: string;
  userId: string;
};

export type ApproveRejoinResponse = {
  success: boolean;
  nowFull: boolean;
};

export async function approveRejoinRequest(
  payload: ApproveRejoinRequest,
): Promise<ApproveRejoinResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<ApproveRejoinRequest, ApproveRejoinResponse>(
    functions,
    "approveRejoinRequest",
  );
  try {
    await analytics.logEvent("call_function", { function: "approveRejoinRequest", payload });
    await crashlytics.log(`call_function:approveRejoinRequest:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "approveRejoinRequest", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "approveRejoinRequest", e);
    throw e;
  }
}

type DeclineRejoinRequest = {
  activityId: string;
  userId: string;
};

export type DeclineRejoinResponse = {
  success: boolean;
};

export async function declineRejoinRequest(
  payload: DeclineRejoinRequest,
): Promise<DeclineRejoinResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<DeclineRejoinRequest, DeclineRejoinResponse>(
    functions,
    "declineRejoinRequest",
  );
  try {
    await analytics.logEvent("call_function", { function: "declineRejoinRequest", payload });
    await crashlytics.log(`call_function:declineRejoinRequest:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "declineRejoinRequest", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "declineRejoinRequest", e);
    throw e;
  }
}

type DeleteActivityRequest = {
  activityId: string;
};

export type DeleteActivityResponse = {
  success: boolean;
  message: string;
};

export async function deleteActivity(
  payload: DeleteActivityRequest,
): Promise<DeleteActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<DeleteActivityRequest, DeleteActivityResponse>(
    functions,
    "deleteActivity",
  );
  try {
    await analytics.logEvent("call_function", { function: "deleteActivity", payload });
    await crashlytics.log(`call_function:deleteActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "deleteActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "deleteActivity", e);
    throw e;
  }
}

type UndeleteActivityRequest = {
  activityId: string;
};

export type UndeleteActivityResponse = {
  success: boolean;
  message: string;
};

export async function undeleteActivity(
  payload: UndeleteActivityRequest,
): Promise<UndeleteActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UndeleteActivityRequest,
    UndeleteActivityResponse
  >(functions, "undeleteActivity");
  try {
    await analytics.logEvent("call_function", { function: "undeleteActivity", payload });
    await crashlytics.log(`call_function:undeleteActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "undeleteActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "undeleteActivity", e);
    throw e;
  }
}

type InviteUsersToActivityRequest = {
  activityId: string;
  userIds: string[];
};

export type InviteUsersToActivityResponse = {
  success: boolean;
  message: string;
};

export async function inviteUsersToActivity(
  payload: InviteUsersToActivityRequest,
): Promise<InviteUsersToActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    InviteUsersToActivityRequest,
    InviteUsersToActivityResponse
  >(functions, "inviteUsersToActivity");
  try {
    await analytics.logEvent("call_function", { function: "inviteUsersToActivity", payload });
    await crashlytics.log(`call_function:inviteUsersToActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "inviteUsersToActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "inviteUsersToActivity", e);
    throw e;
  }
}

type CancelInviteRequest = {
  activityId: string;
  userId: string;
};

export type CancelInviteResponse = {
  success: boolean;
  message: string;
};

export async function cancelInvite(
  payload: CancelInviteRequest,
): Promise<CancelInviteResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<CancelInviteRequest, CancelInviteResponse>(
    functions,
    "cancelInvite",
  );
  try {
    await analytics.logEvent("call_function", { function: "cancelInvite", payload });
    await crashlytics.log(`call_function:cancelInvite:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "cancelInvite", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "cancelInvite", e);
    throw e;
  }
}

type SearchActivitiesRequest = {
  query?: string;
  filters?: {
    category?: string;
    includePrivate?: boolean;
  };
  pageSize?: number;
};

export type SearchActivitiesResponse = {
  success: boolean;
  results: ActivityPayload[];
  hasMore: boolean;
  count: number;
};

export async function searchActivities(
  payload: SearchActivitiesRequest = {},
): Promise<SearchActivitiesResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    SearchActivitiesRequest,
    SearchActivitiesResponse
  >(functions, "searchActivities");
  try {
    await analytics.logEvent("call_function", { function: "searchActivities", payload });
    await crashlytics.log(`call_function:searchActivities:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "searchActivities", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "searchActivities", e);
    throw e;
  }
}

type GetUserProfileRequest = {
  userId: string;
};

export type UserProfile = {
  id: string;
  username: string;
  email: string;
  avatar: string;
  bio: string;
  location: string;
  passions: string[];
  followers: string[];
  following: string[];
  createdAt: string | null;
  updatedAt: string | null;
  vibeScore: number;
  trustScore: number;
  profileComplete: boolean;
  isVerified: boolean;
  isPrivate: boolean;
};

export type GetUserProfileResponse = {
  success: boolean;
  profile: UserProfile;
};

export async function getUserProfile(
  payload: GetUserProfileRequest,
): Promise<GetUserProfileResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<GetUserProfileRequest, GetUserProfileResponse>(
    functions,
    "getUserProfile",
  );
  try {
    await analytics.logEvent("call_function", { function: "getUserProfile", payload });
    await crashlytics.log(`call_function:getUserProfile:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getUserProfile", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getUserProfile", e);
    throw e;
  }
}

type SearchUsersRequest = {
  query: string;
  pageSize?: number;
};

export type SearchUsersResult = {
  id: string;
  username: string;
  avatar: string;
  bio: string;
  location: string;
  vibeScore: number;
  followers: number;
  following: number;
  isVerified: boolean;
};

export type SearchUsersResponse = {
  success: boolean;
  results: SearchUsersResult[];
  count: number;
};

export async function searchUsers(
  payload: SearchUsersRequest,
): Promise<SearchUsersResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<SearchUsersRequest, SearchUsersResponse>(
    functions,
    "searchUsers",
  );
  try {
    await analytics.logEvent("call_function", { function: "searchUsers", payload });
    await crashlytics.log(`call_function:searchUsers:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "searchUsers", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "searchUsers", e);
    throw e;
  }
}

type GetActivityReportsRequest = {
  activityId: string;
};

export type ActivityReport = {
  id: string;
  reporterId: string;
  reason: string;
  description: string;
  type: string;
  status: string;
  createdAt: string | null;
};

export type GetActivityReportsResponse = {
  success: boolean;
  activityId: string;
  activityTitle: string;
  reports: ActivityReport[];
  summary: {
    totalReports: number;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
  };
};

export async function getActivityReports(
  payload: GetActivityReportsRequest,
): Promise<GetActivityReportsResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    GetActivityReportsRequest,
    GetActivityReportsResponse
  >(functions, "getActivityReports");
  try {
    await analytics.logEvent("call_function", { function: "getActivityReports", payload });
    await crashlytics.log(`call_function:getActivityReports:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "getActivityReports", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "getActivityReports", e);
    throw e;
  }
}

// ====== INVITE & REPORT FUNCTIONS ======

export type RespondToInviteRequest = {
  activityId: string;
  accept: boolean;
};

export type RespondToInviteResponse = {
  success: boolean;
  action: "accepted" | "declined";
};

export async function respondToInvite(
  payload: RespondToInviteRequest,
): Promise<RespondToInviteResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    RespondToInviteRequest,
    RespondToInviteResponse
  >(functions, "respondToInvite");
  try {
    await analytics.logEvent("call_function", { function: "respondToInvite", payload });
    await crashlytics.log(`call_function:respondToInvite:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "respondToInvite", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "respondToInvite", e);
    throw e;
  }
}

export type ReportActivityRequest = {
  activityId: string;
  reason: string;
  deviceInfo?: {
    platform?: string;
    deviceModel?: string;
    deviceOsVersion?: string;
    deviceManufacturer?: string;
    deviceName?: string;
    userAgent?: string;
    appVersion?: string;
  };
};

export type ReportActivityResponse = {
  success: boolean;
  message: string;
};

export async function reportActivity(
  payload: ReportActivityRequest,
): Promise<ReportActivityResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<ReportActivityRequest, ReportActivityResponse>(
    functions,
    "reportActivity",
  );
  try {
    await analytics.logEvent("call_function", { function: "reportActivity", payload });
    await crashlytics.log(`call_function:reportActivity:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "reportActivity", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "reportActivity", e);
    throw e;
  }
}

// ====== CHAT FUNCTIONS ======

export type SendChatMessageRequest = {
  chatId: string;
  clientRequestId?: string;
  text: string;
  format?: "plain" | "whatsapp";
  media?: {
    id: string;
    type: "image" | "video" | "audio" | "document";
    url: string;
    mimeType: string;
    fileName: string;
    size: number;
  }[];
  location?: {
    kind: "static" | "live";
    latitude: number;
    longitude: number;
    label?: string;
    placeName?: string;
    accuracyMeters?: number;
    sharedAt: string;
    liveShareId?: string;
    expiresAt?: string;
  } | null;
  replyTo?: {
    messageId: string;
    senderId: string;
    senderUsername: string;
    text: string;
  } | null;
  isForwarded?: boolean;
};

export type SendChatMessageResponse = {
  success: boolean;
  messageId: string;
  createdAt: string;
};

export async function sendChatMessage(
  payload: SendChatMessageRequest,
): Promise<SendChatMessageResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    SendChatMessageRequest,
    SendChatMessageResponse
  >(functions, "sendChatMessage");
  try {
    await analytics.logEvent("call_function", { function: "sendChatMessage", payload });
    await crashlytics.log(`call_function:sendChatMessage:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "sendChatMessage", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "sendChatMessage", e);
    throw e;
  }
}

export type UpdateChatMessageRequest = {
  chatId: string;
  messageId: string;
  action: "edit" | "delete_for_me" | "delete_for_everyone";
  text?: string;
  format?: "plain" | "whatsapp";
};

export type UpdateChatMessageResponse = {
  success: boolean;
  action: "edit" | "delete_for_me" | "delete_for_everyone";
  messageId: string;
  updatedAt: string;
};

export async function updateChatMessage(
  payload: UpdateChatMessageRequest,
): Promise<UpdateChatMessageResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UpdateChatMessageRequest,
    UpdateChatMessageResponse
  >(functions, "updateChatMessage");
  try {
    await analytics.logEvent("call_function", { function: "updateChatMessage", payload });
    await crashlytics.log(`call_function:updateChatMessage:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "updateChatMessage", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "updateChatMessage", e);
    throw e;
  }
}

export type UpdateMessageReactionRequest = {
  chatId: string;
  messageId: string;
  emoji: string;
  action: "add" | "remove";
};

export type UpdateMessageReactionResponse = {
  success: boolean;
  action: "add" | "remove";
  emoji: string;
  reactors: string[];
};

export async function updateMessageReaction(
  payload: UpdateMessageReactionRequest,
): Promise<UpdateMessageReactionResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    UpdateMessageReactionRequest,
    UpdateMessageReactionResponse
  >(functions, "updateMessageReaction");
  try {
    await analytics.logEvent("call_function", { function: "updateMessageReaction", payload });
    await crashlytics.log(`call_function:updateMessageReaction:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "updateMessageReaction", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "updateMessageReaction", e);
    throw e;
  }
}

export type SyncChatActivityStatusRequest = {
  mismatches: { chatId: string; activityStatus: "active" | "inactive" }[];
};

export type SyncChatActivityStatusResponse = {
  success: boolean;
  updated: number;
  skipped: number;
  requested: number;
  truncated?: boolean;
};

export async function syncChatActivityStatus(
  payload: SyncChatActivityStatusRequest,
): Promise<SyncChatActivityStatusResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    SyncChatActivityStatusRequest,
    SyncChatActivityStatusResponse
  >(functions, "syncChatActivityStatus");
  try {
    await analytics.logEvent("call_function", { function: "syncChatActivityStatus", payload });
    await crashlytics.log(`call_function:syncChatActivityStatus:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "syncChatActivityStatus", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "syncChatActivityStatus", e);
    throw e;
  }
}

export type MarkChatMessagesAsReadRequest = {
  chatId: string;
  messageIds?: string[];
};

export type MarkChatMessagesAsReadResponse = {
  success: boolean;
  markedCount: number;
  type: "all" | "specific";
};

export async function markChatMessagesAsRead(
  payload: MarkChatMessagesAsReadRequest,
): Promise<MarkChatMessagesAsReadResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    MarkChatMessagesAsReadRequest,
    MarkChatMessagesAsReadResponse
  >(functions, "markChatMessagesAsRead");
  try {
    await analytics.logEvent("call_function", { function: "markChatMessagesAsRead", payload });
    await crashlytics.log(`call_function:markChatMessagesAsRead:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "markChatMessagesAsRead", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "markChatMessagesAsRead", e);
    throw e;
  }
}

export type ToggleStarMessageRequest = {
  chatId: string;
  messageId: string;
};

export type ToggleStarMessageResponse = {
  success: boolean;
  chatId: string;
  messageId: string;
  starred: boolean;
};

export async function toggleStarMessage(
  payload: ToggleStarMessageRequest,
): Promise<ToggleStarMessageResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    ToggleStarMessageRequest,
    ToggleStarMessageResponse
  >(functions, "toggleStarMessage");
  try {
    await analytics.logEvent("call_function", { function: "toggleStarMessage", payload });
    await crashlytics.log(`call_function:toggleStarMessage:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "toggleStarMessage", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "toggleStarMessage", e);
    throw e;
  }
}

export type ForwardMessagesRequest = {
  sourceChatId: string;
  messageIds: string[];
  targetChatIds: string[];
};

export type ForwardMessagesResponse = {
  success: boolean;
  sourceChatId: string;
  targetCount: number;
  forwardedCount: number;
};

export async function forwardMessages(
  payload: ForwardMessagesRequest,
): Promise<ForwardMessagesResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    ForwardMessagesRequest,
    ForwardMessagesResponse
  >(functions, "forwardMessages");
  try {
    await analytics.logEvent("call_function", { function: "forwardMessages", payload });
    await crashlytics.log(`call_function:forwardMessages:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "forwardMessages", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "forwardMessages", e);
    throw e;
  }
}

// ====== NOTIFICATION FUNCTIONS ======

export type MarkNotificationsAsReadRequest = {
  type?: string;
  fromUserId?: string;
  all?: boolean;
};

export type MarkNotificationsAsReadResponse = {
  success: boolean;
  markedCount: number;
  criteria?: {
    type?: string;
    fromUserId?: string;
    all?: boolean;
  };
};

export async function markNotificationsAsRead(
  payload: MarkNotificationsAsReadRequest,
): Promise<MarkNotificationsAsReadResponse> {
  if (!functions) throw new Error("Firebase Functions not initialized");
  const callable = httpsCallable<
    MarkNotificationsAsReadRequest,
    MarkNotificationsAsReadResponse
  >(functions, "markNotificationsAsRead");
  try {
    await analytics.logEvent("call_function", { function: "markNotificationsAsRead", payload });
    await crashlytics.log(`call_function:markNotificationsAsRead:${JSON.stringify(payload)}`);
  } catch (e) {
    crashlytics.logError("Analytics logEvent failed", "markNotificationsAsRead", e);
  }
  try {
    const result = await callable(payload);
    return result.data;
  } catch (e) {
    crashlytics.logError("Function call failed", "markNotificationsAsRead", e);
    throw e;
  }
}
